import { Modal, Table, message } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import dayjs from 'dayjs';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePapaParse } from 'react-papaparse';

import NoDataEmpty from '../../components/NoDataEmpty';
import { downloadCsv } from '../../utils/csv';
import OpenClassAPI from '../OpenClassAPI';
import {
  AttendanceStatus,
  CertificateResultStatus,
  OpenClass,
  OpenClassRegistration,
  OpenClassSession,
} from '../types/OpenClass';
import { useAttendeeCertificateActions } from '../hooks/useAttendeeCertificateActions';
import {
  StatusAction,
  splitByCertificateIssued,
} from '../utils/attendeeStatusActions';
import { certificateTypeLabel } from '../utils/certificateTypeLabel';
import {
  AttendeeCompanyFilter,
  attendeeCompanyFilterOptions,
  filterAttendeesByCompany,
  sortAttendeesByCompany,
} from '../utils/attendeeCompany';
import {
  SessionAttendee,
  sessionAttendeeCsvColumns,
  sessionAttendeeCsvRows,
  sessionAttendeeStatus,
  sessionAttendees,
} from '../utils/sessionAttendees';
import { AttendeeSelectionBar } from './AttendeeActions';
import AttendeeListToolbar from './AttendeeListToolbar';
import {
  attendeeActionsColumn,
  attendeeBesaccCertificateColumn,
  attendeeCertificateSentColumn,
  attendeeCertificatesColumn,
  attendeeCompanyColumn,
  attendeeNameColumn,
} from './attendeeColumns';
import OpenClassCertificateLanguageModal from './OpenClassCertificateLanguageModal';
import BesaccCertificatePreviewModal from './BesaccCertificatePreviewModal';
import OpenClassAttendeeStatusBox from './OpenClassAttendeeStatusBox';

type OpenClassSessionAttendeesModalProps = {
  canUpdate: boolean;
  openClass: OpenClass;
  open: boolean;
  registrations: OpenClassRegistration[];
  session: OpenClassSession;
  onClose: () => void;
  onUpdated: () => Promise<void> | void;
};

const OpenClassSessionAttendeesModal: React.FC<
  OpenClassSessionAttendeesModalProps
> = ({
  canUpdate,
  openClass,
  open,
  registrations,
  session,
  onClose,
  onUpdated,
}) => {
  const certificateTypes = useMemo(
    () => openClass.certificateTemplates.map(({ type }) => type),
    [openClass.certificateTemplates],
  );
  const { t } = useTranslation();
  const { jsonToCSV } = usePapaParse();
  const [selectedIds, setSelectedIds] = useState<React.Key[]>([]);
  const [selectedCompanies, setSelectedCompanies] = useState<
    AttendeeCompanyFilter[] | null
  >(null);
  const [selectedStatuses, setSelectedStatuses] = useState<string[] | null>(
    null,
  );
  const [selectedCertificateTypes, setSelectedCertificateTypes] = useState<
    string[] | null
  >(null);

  const clearSelection = useCallback(() => setSelectedIds([]), []);

  const {
    besaccPreview,
    canIssueCertificate,
    cancelLanguagePrompt,
    certificateLanguages,
    closeBesaccPreview,
    confirmLanguagePrompt,
    confirmEmailCertificate,
    confirmEmailCertificates,
    downloadCertificates,
    languagePromptOpen,
    previewBesaccCertificate,
    removeBesaccCertificate,
    runUpdates,
    saving,
    uploadBesaccCertificate,
  } = useAttendeeCertificateActions({
    clearSelection,
    openClass,
    registrations,
    onUpdated,
  });

  const attendees = useMemo(
    () => sessionAttendees(registrations, session.id),
    [registrations, session.id],
  );

  const selectedAttendees = useMemo(() => {
    const selected = new Set(selectedIds);
    return attendees.filter((attendee) =>
      selected.has(attendee.registrationId),
    );
  }, [attendees, selectedIds]);

  const selectedCertificateCount = useMemo(
    () => selectedAttendees.filter(canIssueCertificate).length,
    [canIssueCertificate, selectedAttendees],
  );

  const companyFilterOptions = useMemo(
    () =>
      attendeeCompanyFilterOptions(
        attendees,
        t('components.OpenClassAttendeeActions.privateParticipant'),
      ),
    [attendees, t],
  );

  const filteredAttendees = useMemo(() => {
    const companyFiltered = filterAttendeesByCompany(
      attendees,
      selectedCompanies,
    );
    return companyFiltered.filter(
      (attendee) =>
        (!selectedStatuses?.length ||
          selectedStatuses.includes(sessionAttendeeStatus(attendee))) &&
        (!selectedCertificateTypes?.length ||
          selectedCertificateTypes.includes(attendee.certificateType)),
    );
  }, [
    attendees,
    selectedCertificateTypes,
    selectedCompanies,
    selectedStatuses,
  ]);

  const displayedAttendees = useMemo(
    () => sortAttendeesByCompany(filteredAttendees),
    [filteredAttendees],
  );

  const statusOptions = useMemo(
    () =>
      [...new Set(attendees.map(sessionAttendeeStatus))]
        .sort()
        .map((status) => ({
          label: t(`components.OpenClassAttendeeStatusBox.${status}`),
          value: status,
        })),
    [attendees, t],
  );

  const certificateTypeOptions = useMemo(
    () =>
      certificateTypes.map((certificateType) => ({
        label: certificateTypeLabel(certificateType),
        value: certificateType,
      })),
    [certificateTypes],
  );

  const applyStatus = useCallback(
    async (
      action: StatusAction,
      attendees: SessionAttendee[],
      onApplied?: () => void,
    ) => {
      const { writable, locked } = splitByCertificateIssued(attendees);
      if (locked.length > 0) {
        message.warning(
          t('components.OpenClassAttendeeActions.certificateIssuedSkipped', {
            count: locked.length,
          }),
        );
      }
      if (writable.length === 0) {
        onApplied?.();
        return;
      }

      await runUpdates(
        writable.map((attendee) =>
          action.field === 'attendanceStatus'
            ? OpenClassAPI.updateSessionAttendance(
                session.id,
                attendee.registrationId,
                { attendanceStatus: action.value as AttendanceStatus },
              )
            : // Passing a day implies being present for it, the same shortcut the instructor app takes
              OpenClassAPI.updateSessionAttendance(
                session.id,
                attendee.registrationId,
                {
                  attendanceStatus:
                    action.key === 'passed'
                      ? 'present'
                      : attendee.attendanceStatus,
                  result: action.value as CertificateResultStatus,
                },
              ),
        ),
      );
      onApplied?.();
    },
    [runUpdates, session.id, t],
  );

  const onExport = useCallback(() => {
    const csv = jsonToCSV(
      sessionAttendeeCsvRows(
        filteredAttendees,
        t('components.OpenClassAttendeeActions.privateParticipant'),
        certificateTypeLabel,
      ),
      {
        columns: sessionAttendeeCsvColumns,
      },
    );
    downloadCsv(
      `${t('components.OpenClassSessionAttendeesModal.title', {
        date: dayjs(session.startAt).format('YYYY-MM-DD'),
      })}.csv`.replace(/[\s:]+/g, '-'),
      csv,
    );
  }, [filteredAttendees, jsonToCSV, session.startAt, t]);

  const columns: ColumnsType<SessionAttendee> = useMemo(
    () => [
      attendeeNameColumn(
        t,
        (attendee) => attendee.attendanceStatus === 'not_present',
      ),
      attendeeCompanyColumn(t),
      {
        title: t('components.OpenClassAttendeeActions.columnStatus'),
        key: 'status',
        render: (_, attendee) => (
          <OpenClassAttendeeStatusBox
            status={sessionAttendeeStatus(attendee)}
          />
        ),
      },
      attendeeCertificatesColumn(t, certificateTypes),
      attendeeBesaccCertificateColumn<SessionAttendee>({
        canUpdate,
        saving,
        t,
        onPreview: previewBesaccCertificate,
        onRemove: (attendee) =>
          void removeBesaccCertificate(attendee.registrationId),
        onUpload: (attendee, file) =>
          void uploadBesaccCertificate(attendee.registrationId, file),
      }),
      attendeeCertificateSentColumn<SessionAttendee>({
        t,
      }),
      attendeeActionsColumn<SessionAttendee>({
        canUpdate,
        canIssueCertificate,
        isAtStatus: (row, action) =>
          action.field === 'attendanceStatus'
            ? row.attendanceStatus === action.value
            : row.sessionResult === action.value,
        t,
        onApplyStatus: applyStatus,
        onDownload: downloadCertificates,
        onEmail: confirmEmailCertificate,
      }),
    ],
    [
      applyStatus,
      canIssueCertificate,
      canUpdate,
      certificateTypes,
      confirmEmailCertificate,
      downloadCertificates,
      previewBesaccCertificate,
      removeBesaccCertificate,
      saving,
      t,
      uploadBesaccCertificate,
    ],
  );

  return (
    <Modal
      footer={null}
      open={open}
      title={t('components.OpenClassSessionAttendeesModal.title', {
        date: dayjs(session.startAt).format('DD MMMM YYYY'),
      })}
      width={1280}
      onCancel={onClose}>
      <AttendeeListToolbar
        companyOptions={companyFilterOptions}
        certificateTypeOptions={certificateTypeOptions}
        selectedCompanies={selectedCompanies}
        selectedCertificateTypes={selectedCertificateTypes}
        selectedStatuses={selectedStatuses}
        statusOptions={statusOptions}
        exportDisabled={filteredAttendees.length === 0}
        onCompaniesChange={(companies) => {
          setSelectedCompanies(companies);
          clearSelection();
        }}
        onCertificateTypesChange={(certificateTypes) => {
          setSelectedCertificateTypes(certificateTypes);
          clearSelection();
        }}
        onExport={onExport}
        onStatusesChange={(statuses) => {
          setSelectedStatuses(statuses);
          clearSelection();
        }}
      />
      <div className="mt-4">
        <Table
          columns={columns}
          dataSource={displayedAttendees}
          loading={saving}
          locale={{ emptyText: <NoDataEmpty /> }}
          rowKey={(attendee) => attendee.registrationId}
          rowSelection={
            canUpdate
              ? {
                  selectedRowKeys: selectedIds,
                  onChange: setSelectedIds,
                  getCheckboxProps: (attendee) => ({
                    disabled: attendee.certificateIssued,
                  }),
                }
              : undefined
          }
        />
      </div>
      {selectedAttendees.length > 0 && (
        <AttendeeSelectionBar
          certificateCount={selectedCertificateCount}
          saving={saving}
          selectedCount={selectedAttendees.length}
          onChangeStatus={(action) =>
            applyStatus(action, selectedAttendees, clearSelection)
          }
          onDownload={() => downloadCertificates(selectedAttendees)}
          onEmail={() => confirmEmailCertificates(selectedAttendees)}
        />
      )}
      <OpenClassCertificateLanguageModal
        languages={certificateLanguages}
        open={languagePromptOpen}
        saving={saving}
        onCancel={cancelLanguagePrompt}
        onConfirm={confirmLanguagePrompt}
      />
      <BesaccCertificatePreviewModal
        certificate={besaccPreview}
        onClose={closeBesaccPreview}
        onRemove={canUpdate ? removeBesaccCertificate : undefined}
        onReplace={canUpdate ? uploadBesaccCertificate : undefined}
      />
    </Modal>
  );
};

export default OpenClassSessionAttendeesModal;
